import Vue from 'vue';
import {NotificationTypes} from '@/enums/notifier/NotificationTypes';
import {NotificationChannelSettings, NotificationSettings, UserPreferences} from '@/interfaces/clientOnly/userPreferences.interface';
import {setThemePreference, THEME_PREFERENCES, ThemePreference, themeState} from '@/helpers/themePreference.helper';
import {applyServerSendDefaults} from '@/helpers/sendDefaultsSync.helper';

/**
 * Notification settings per type (Ton, Pop-up, Browser, aktiv) and the daily digest mail.
 *
 * Stored at the login user via `me/preferences` when the user-login-handler offers it (Notify v2); otherwise – and as
 * cache for the first moments after loading – in localStorage per user. The digest mail needs the backend, so its
 * toggle is only offered when the server stores the settings.
 */

export type NotificationChannel = keyof NotificationChannelSettings;

/**
 * Loaded lazily: this helper is used by the notifier socket, which the router imports – a static import of an API
 * class here would create an import cycle (api.class → router → socket → API).
 */
const userApi = async () => (await import('@/classes/api/user-login-handler.api')).UserLoginHandlerApi;

export const NOTIFICATION_CHANNELS: Array<{ value: NotificationChannel; text: string }> = [
    {value: 'sound', text: 'Ton'},
    {value: 'popup', text: 'Pop-up'},
    {value: 'browser', text: 'Browser'},
];

const DEFAULTS: Required<NotificationChannelSettings> = {sound: true, popup: true, browser: false, enabled: true, digest: false};

/** Types shown in the settings, in this order (`teacherOnly`: only shown to teachers) */
export const NOTIFICATION_TYPE_OPTIONS: Array<{ type: NotificationTypes; text: string; icon: string; teacherOnly?: boolean; qualityOnly?: boolean }> = [
    {type: NotificationTypes.MENTION, text: 'Erwähnungen', icon: 'mdi-at'},
    {type: NotificationTypes.CALL, text: 'Anrufe', icon: 'mdi-phone-incoming-outline'},
    {type: NotificationTypes.TO_DO_ASSIGNED, text: 'To-do zugewiesen', icon: 'mdi-clipboard-account-outline'},
    {type: NotificationTypes.DUE_TO_DO, text: 'To-do heute fällig', icon: 'mdi-calendar-today'},
    {type: NotificationTypes.OVERDUE_TO_DO, text: 'To-do überfällig', icon: 'mdi-alarm'},
    {type: NotificationTypes.TO_DO_COMPLETED, text: 'To-do erledigt', icon: 'mdi-check-circle-outline'},
    {type: NotificationTypes.MISSING_COURSE_LOGS, text: 'Fehlende Protokolle (morgens)', icon: 'mdi-clipboard-clock-outline', teacherOnly: true},
    {type: NotificationTypes.QUALITY_CASE_DIGEST, text: 'QM-Cockpit: neue Fälle (Zusammenfassung)', icon: 'mdi-account-heart-outline', qualityOnly: true},
    {type: NotificationTypes.ENTITY_ASSIGNED_TO_SUPERVISOR, text: 'Neue Zuweisung', icon: 'mdi-account-arrow-left-outline'},
    {type: NotificationTypes.SUPERVISED_ENTITY_DEACTIVATED, text: 'Deaktivierung betreuter Profile', icon: 'mdi-account-off-outline'},
    {type: NotificationTypes.HANDOUT_EVENT_FEEDBACK, text: 'Feedback zu Austeilaktionen', icon: 'mdi-message-reply-text-outline'},
    {type: NotificationTypes.APPLICATION_WITHDRAWN, text: 'Bewerbung zurückgezogen', icon: 'mdi-account-arrow-left-outline'},
    {type: NotificationTypes.TAKEOVER_OFFER_UPDATE, text: 'Übernahme-Angebote (Zusagen, Absagen)', icon: 'mdi-account-convert-outline'},
    {type: NotificationTypes.TEACHER_SICK_REPORT, text: 'Ausfälle von Lehrkräften (Krankmeldung, zurückgezogene Vertretung)', icon: 'mdi-account-alert-outline'},
    {type: NotificationTypes.COURSE_CANCELLATION_FAILED, text: 'Fehler bei Kursabsagen', icon: 'mdi-alert-outline'},
    {type: NotificationTypes.WHATSAPP_MESSAGE_TEMPLATE_STATUS_UPDATE, text: 'WhatsApp-Vorlagen', icon: 'mdi-whatsapp'},
];

export type BrowserPermission = NotificationPermission | 'unsupported';

const browserPermission = (): BrowserPermission => {
    try {
        return typeof window !== 'undefined' && 'Notification' in window ? window.Notification.permission : 'unsupported';
    } catch (e) {
        return 'unsupported';
    }
};

export const notificationPreferences = Vue.observable({
    settings: {} as NotificationSettings,
    /** true: stored at the user (me/preferences), false: only in this browser */
    serverSupported: false,
    loaded: false,
    userId: undefined as string | undefined,
    browserPermission: browserPermission(),
});

const storageKey = (userId?: string) => `vanilla.notify.settings.${userId || 'anonymous'}`;

const readLocal = (userId?: string): NotificationSettings => {
    try {
        const raw = localStorage.getItem(storageKey(userId));
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed as NotificationSettings : {};
    } catch (e) {
        return {};
    }
};

const writeLocal = (settings: NotificationSettings, userId?: string) => {
    try {
        localStorage.setItem(storageKey(userId), JSON.stringify(settings));
    } catch (e) {
        // storage blocked: settings last for this page view
    }
};

/** Effective value of a channel of a type (defaults: Ton, Pop-up and aktiv on, Browser off) */
export function channelEnabled(type: string, channel: NotificationChannel): boolean {
    const entry = notificationPreferences.settings[type] as NotificationChannelSettings | undefined;
    const value = entry?.[channel];
    return typeof value === 'boolean' ? value : DEFAULTS[channel];
}

export function digestEnabled(): boolean {
    return notificationPreferences.settings.digest?.email === true;
}

/**
 * Loads the settings of the logged-in user: localStorage first (instant), then the server when available.
 * Also applies a theme stored at the user (other device) – localStorage stays the first-paint fallback.
 */
export async function loadUserPreferences(userId?: unknown): Promise<UserPreferences | null> {
    const id = userId ? String(userId) : undefined;
    notificationPreferences.userId = id;
    notificationPreferences.settings = readLocal(id);
    const server = await (await userApi()).getMyPreferences();
    notificationPreferences.serverSupported = !!server;
    if (server?.notificationSettings) {
        notificationPreferences.settings = {...server.notificationSettings};
        writeLocal(notificationPreferences.settings, id);
    }
    const theme = server?.uiPreferences?.theme;
    if (theme && THEME_PREFERENCES.includes(theme as ThemePreference)) {
        setThemePreference(theme as ThemePreference, id);
    } else if (server && themeState.preference !== 'system') {
        // first device with server support: take over the choice made in this browser
        void userApi().then((api) => api.updateMyPreferences({uiPreferences: {theme: themeState.preference}}));
    }
    // "Meine Versand-Standards": server -> local cache, one-time migration of browser-only defaults
    void applyServerSendDefaults(id, server).catch(() => undefined);
    notificationPreferences.loaded = true;
    return server;
}

/** Changes one channel of a type; saved at the user when possible, always in this browser */
export async function setChannel(type: string, channel: NotificationChannel, value: boolean): Promise<boolean> {
    const entry = {...((notificationPreferences.settings[type] as NotificationChannelSettings | undefined) || {}), [channel]: value};
    notificationPreferences.settings = {...notificationPreferences.settings, [type]: entry};
    writeLocal(notificationPreferences.settings, notificationPreferences.userId);
    if (!notificationPreferences.serverSupported) {
        return true;
    }
    return (await userApi()).updateMyPreferences({notificationSettings: {[type]: {[channel]: value}}});
}

/** Daily summary mail (only with server support) */
export async function setDigest(email: boolean): Promise<boolean> {
    notificationPreferences.settings = {...notificationPreferences.settings, digest: {email}};
    writeLocal(notificationPreferences.settings, notificationPreferences.userId);
    return (await userApi()).updateMyPreferences({notificationSettings: {digest: {email}}});
}

/** Stores the theme at the user as well (theme itself is applied by themePreference.helper) */
export function saveThemeAtUser(theme: ThemePreference): void {
    if (notificationPreferences.serverSupported) {
        void userApi().then((api) => api.updateMyPreferences({uiPreferences: {theme}}));
    }
}

/* ------------------------- browser notifications ------------------------- */

export async function requestBrowserPermission(): Promise<BrowserPermission> {
    if (browserPermission() === 'unsupported') {
        return 'unsupported';
    }
    try {
        notificationPreferences.browserPermission = await window.Notification.requestPermission();
    } catch (e) {
        notificationPreferences.browserPermission = browserPermission();
    }
    return notificationPreferences.browserPermission;
}

/** System notification while the tab is in the background (after opt-in) */
export function showBrowserNotification(title: string, body: string | undefined, tag: string | undefined, onClick?: () => void): void {
    if (browserPermission() !== 'granted' || typeof document === 'undefined' || !document.hidden) {
        return;
    }
    try {
        const notification = new window.Notification(title, {body: body?.slice(0, 180), tag, icon: '/favicon.ico'});
        notification.onclick = () => {
            window.focus();
            onClick?.();
            notification.close();
        };
    } catch (e) {
        // e.g. mobile browsers that only allow notifications from a service worker
    }
}
